import { Plain, BookingApi } from '@agendor/shared';
import { initClient } from '@ts-rest/core';
import AuthService, { SessionExpiredError } from './auth-service';

export type BusinessInfo = BookingApi.GetBusinessInfoResponse;
export type Service = BookingApi.Service;
export type Worker = BookingApi.Worker;
export type Booking = BookingApi.Booking;
export type TimeSlot = Plain.TimeOfDay;
export type ClientSubscriptionInfo = BookingApi.ClientSubscription;

class ApiError extends Error {
    constructor(
        readonly status: number,
        readonly responseBody: unknown,
    ) {
        super(`API error: ${status}`);
    }
}

export default class BookingService {
    private _serverRootPath: string;
    private _authService: AuthService;
    private _client;

    constructor(params: { serverRootPath: string; authService: AuthService }) {
        this._serverRootPath = params.serverRootPath;
        this._authService = params.authService;

        this._client = initClient(BookingApi.contract, {
            baseUrl: `${params.serverRootPath}/booking`,
            api: async (args) => {
                const response = await params.authService.authenticatedFetch(args.path, {
                    method: args.method,
                    headers: args.headers as Record<string, string>,
                    body: args.body,
                });

                const body = response.status === 204 ? undefined : await response.json().catch(() => undefined);

                if (!response.ok) throw new ApiError(response.status, body);

                return { status: response.status, body, headers: response.headers };
            },
        });
    }

    async getBusinessInfo(slug: string): Promise<BusinessInfo> {
        const url = `${this._serverRootPath}/booking/${slug}`;

        const fetchBusiness = async (withAuth: boolean): Promise<BusinessInfo> => {
            const response = withAuth
                ? await this._authService.authenticatedFetch(url)
                : await fetch(url, { credentials: 'include' });

            if (response.status === 404) throw new BusinessNotFoundError();
            if (!response.ok) throw new Error('Failed to get business info');

            return await response.json();
        };

        const hasToken = this._authService.accessToken !== null;
        if (hasToken) {
            try {
                return await fetchBusiness(true);
            } catch (error) {
                if (error instanceof BusinessNotFoundError) throw error;
                if (!(error instanceof SessionExpiredError)) {
                    throw error;
                }
                this._authService.clearAccessToken();
            }
        }

        return fetchBusiness(false);
    }

    async setClientName(slug: string, name: string): Promise<{ id: number; accessToken: string }> {
        try {
            const result = await this._client.setClientName({
                params: { slug },
                body: { name },
            });
            return { id: result.body.id, accessToken: result.body.accessToken };
        } catch (error) {
            if (error instanceof ApiError) throw new Error('Failed to set client name');
            throw error;
        }
    }

    async getBookings(slug: string): Promise<Booking[]> {
        try {
            const result = await this._client.getBookings({
                params: { slug },
            });
            return result.body;
        } catch (error) {
            if (error instanceof ApiError) throw new Error('Failed to get bookings');
            throw error;
        }
    }

    async cancelBooking(slug: string, bookingId: string): Promise<void> {
        try {
            await this._client.cancelBooking({
                params: { slug, bookingId },
            });
        } catch (error) {
            if (error instanceof ApiError) {
                if (error.status === 409) {
                    const body = error.responseBody as { code?: string } | undefined;
                    if (body?.code === 'payment_just_confirmed') throw new PaymentJustConfirmedError();
                }
                throw new Error('Failed to cancel booking');
            }
            throw error;
        }
    }

    async confirmBooking(slug: string, bookingId: string): Promise<void> {
        try {
            await this._client.confirmBooking({
                params: { slug, bookingId },
            });
        } catch (error) {
            if (error instanceof ApiError) throw new Error('Failed to confirm booking');
            throw error;
        }
    }

    async getAvailability(
        slug: string,
        params: { date: string; servicesIds: number[]; workerId?: number },
    ): Promise<TimeSlot[]> {
        try {
            const query: { date: string; servicesIds: string; workerId?: string } = {
                date: params.date,
                servicesIds: params.servicesIds.join(','),
            };
            if (params.workerId !== undefined) {
                query.workerId = String(params.workerId);
            }

            const result = await this._client.getAvailability({
                params: { slug },
                query,
            });
            return result.body.slots;
        } catch (error) {
            if (error instanceof ApiError) throw new Error('Failed to get availability');
            throw error;
        }
    }

    async createBooking(
        slug: string,
        params: {
            workerId: number | null;
            servicesIds: number[];
            date: { year: number; month: number; day: number };
            time: { hour: number; minute: number };
            notes: string;
            cpfCnpj?: string;
        },
    ): Promise<Booking> {
        try {
            const result = await this._client.createBooking({
                params: { slug },
                body: params,
            });
            return result.body;
        } catch (error) {
            if (error instanceof ApiError) {
                if (error.status === 409) {
                    const body = error.responseBody as { code?: string } | undefined;
                    if (body?.code === 'max_concurrent_bookings') throw new MaxConcurrentBookingsError();
                    if (body?.code === 'unavailable_time') throw new UnavailableTimeError();
                }
                if (error.status === 502) {
                    const body = error.responseBody as { code?: string } | undefined;
                    if (body?.code === 'payment_failed') throw new PaymentFailedError();
                }
                throw new Error('Failed to create booking');
            }
            throw error;
        }
    }

    async getIntake(slug: string, bookingId: string): Promise<BookingApi.BookingIntake[]> {
        try {
            const result = await this._client.getIntake({
                params: { slug, bookingId },
            });
            return result.body.intakes;
        } catch (error) {
            if (error instanceof ApiError) throw new Error('Failed to get intake');
            throw error;
        }
    }

    async submitIntake(slug: string, bookingId: string, intakes: BookingApi.IntakeSubmission[]): Promise<void> {
        try {
            await this._client.submitIntake({
                params: { slug, bookingId },
                body: { intakes },
            });
        } catch (error) {
            if (error instanceof ApiError) throw new Error('Failed to submit intake');
            throw error;
        }
    }

    async getSubscription(slug: string): Promise<ClientSubscriptionInfo | null> {
        try {
            const result = await this._client.getSubscription({
                params: { slug },
            });
            return result.body.subscription ?? null;
        } catch (error) {
            if (error instanceof ApiError) throw new Error('Failed to get subscription');
            throw error;
        }
    }

    async cancelSubscription(slug: string): Promise<void> {
        try {
            await this._client.cancelSubscription({
                params: { slug },
            });
        } catch (error) {
            if (error instanceof ApiError) throw new Error('Failed to cancel subscription');
            throw error;
        }
    }
}

export class BusinessNotFoundError extends Error {}
export class MaxConcurrentBookingsError extends Error {}
export class UnavailableTimeError extends Error {}
export class PaymentFailedError extends Error {}
export class PaymentJustConfirmedError extends Error {}
